import {
  BOOST_TRAIL,
  COLOR_MATCH,
  DEATH_BURST,
  EJECT,
  FOOD,
  PLAYER,
  POWERUP,
  TEAM_HUES,
  WORLD,
  matchMultiplier,
  radiusToMass,
} from '../constants';
import { SnakeAI } from '../ai/SnakeAI';
import { ParticleSystem } from '../effects/Particles';
import { FoodField } from '../entities/Food';
import { PowerupField, type Powerup, type PowerupKind } from '../entities/Powerup';
import { Snake, type SnakeSkin, type Personality, type SkinPattern } from '../entities/Snake';
import { hashStringToSeed, mulberry32, randRange } from '../utils/rng';
import { SpatialHash } from '../utils/spatial';

/**
 * Anonymous "filler" bots — they populate the world for hunting & action.
 * They start small, can never compete with rivals on mass, and respawn with
 * a brand-new random identity (no persistent slot). Hue-palette source is
 * the standard TEAM_HUES so the color economy still works on them.
 */
const FILLER_COUNT = 14;
const FILLER_NAMES = [
  'zen', 'mira', 'tide', 'rune', 'glyph', 'helix', 'onyx', 'azure', 'cobra',
  'volt', 'wisp', 'quasar', 'sable', 'tessera', 'umbra', 'plasma', 'xeno', 'amber',
];

/**
 * Fixed 7-rival roster.
 *
 * Each entry is a hand-tuned visual signature: hue + saturation/lightness +
 * brightness-wave parameters + dark-band parameters. No two profiles share a
 * hue, no two share a design — every rival in the leaderboard is instantly
 * distinguishable.
 *
 * Order maps to leaderboard color presentation when no one's eaten yet.
 */
interface RivalProfile {
  name: string;
  hue: number;
  personality: Personality;
  skin: Omit<SnakeSkin, 'hue'>;
}
const RIVAL_PROFILES: ReadonlyArray<RivalProfile> = [
  {
    // Red-orange · slow vibrant breather with floating plasma orbs — "the lantern"
    name: 'LUMEN',
    hue: 20,
    personality: 'cautious',
    skin: {
      sat: 100, baseLight: 54,
      lightAmp: 30, waveFreq: 3,  waveSpeed: 0.35, wavePhase: 0.0,
      darkBandDepth: 0,   darkBandFreq: 0,  darkBandSpeed: 0,    darkBandPhase: 0,
      orbCount: 5,        orbSpeed: 0.16,
      pattern: 'bio',     // organic warm "lantern" glow
    },
  },
  {
    // Amber-yellow · fast strobe + fine traveling lattice — "the circuit"
    name: 'FLUX',
    hue: 65,
    personality: 'normal',
    skin: {
      sat: 100, baseLight: 55,
      lightAmp: 26, waveFreq: 7,  waveSpeed: 1.0, wavePhase: 1.7,
      darkBandDepth: 0.4, darkBandFreq: 18, darkBandSpeed: 0.55, darkBandPhase: 0.6,
      orbCount: 0,        orbSpeed: 0,
      pattern: 'geometric', // circuit = wireframe mesh
    },
  },
  {
    // Lime-green · medium current + bold static tiger stripes — "the tiger"
    name: 'KAIRO',
    hue: 110,
    personality: 'aggressive',
    skin: {
      sat: 100, baseLight: 50,
      lightAmp: 22, waveFreq: 5,  waveSpeed: 0.5, wavePhase: 3.1,
      darkBandDepth: 0.65, darkBandFreq: 11, darkBandSpeed: 0.05, darkBandPhase: 1.2,
      orbCount: 0,         orbSpeed: 0,
      pattern: 'glitch',  // aggressive chaotic energy
    },
  },
  {
    // Teal · moody low-amp + heavy slow bands + slow orbs — "the lurker"
    name: 'NYX',
    hue: 155,
    personality: 'hunter',
    skin: {
      sat: 95, baseLight: 48,
      lightAmp: 16, waveFreq: 2,  waveSpeed: 0.25, wavePhase: 2.0,
      darkBandDepth: 0.7,  darkBandFreq: 5,  darkBandSpeed: 0.15, darkBandPhase: 2.4,
      orbCount: 4,         orbSpeed: -0.08,
      pattern: 'bio',     // hunter camouflage / organic
    },
  },
  // (player gets cyan 200 — see playerHue / spawnPlayer)
  {
    // Indigo · fine forward-flowing lattice — "the racer"
    name: 'ORBIT',
    hue: 245,
    personality: 'normal',
    skin: {
      sat: 100, baseLight: 55,
      lightAmp: 26, waveFreq: 8,  waveSpeed: 0.8, wavePhase: 0.5,
      darkBandDepth: 0.45, darkBandFreq: 22, darkBandSpeed: 0.7, darkBandPhase: 3.0,
      orbCount: 0,         orbSpeed: 0,
      pattern: 'geometric', // lattice runner — fits the tag
    },
  },
  {
    // Violet · big pulsing plasma nodes — "the eye-snake"
    name: 'ECHO',
    hue: 290,
    personality: 'aggressive',
    skin: {
      sat: 100, baseLight: 52,
      lightAmp: 28, waveFreq: 4,  waveSpeed: 0.45, wavePhase: 4.7,
      darkBandDepth: 0.45, darkBandFreq: 7,  darkBandSpeed: 0.2, darkBandPhase: 0.8,
      orbCount: 6,         orbSpeed: 0.22,
      pattern: 'plasma',  // pulsing predator
    },
  },
  {
    // Magenta · pure speed — fast strobe, no bands, no orbs
    name: 'VEX',
    hue: 335,
    personality: 'hunter',
    skin: {
      sat: 100, baseLight: 58,
      lightAmp: 30, waveFreq: 9,  waveSpeed: 1.3, wavePhase: 5.5,
      darkBandDepth: 0,   darkBandFreq: 0,  darkBandSpeed: 0,    darkBandPhase: 0,
      orbCount: 0,        orbSpeed: 0,
      pattern: 'glitch',  // magenta bolt — fast chaos
    },
  },
];

/** Effect callbacks fired by the simulation; the engine wires these to renderers. */
export interface WorldEffects {
  /** Color-match perfect eat — for particle burst + head pulse. */
  onMatchEat: (x: number, y: number, hue: number) => void;
  /** Every eat by any snake — for floating "+N" toasts (engine filters to player). */
  onEat: (snake: Snake, x: number, y: number, hue: number, mass: number, mult: number) => void;
  /** A snake died (collision). `killer` is the snake whose body the
   *  victim crashed into — used by the HUD to surface "killed by X". */
  onSnakeDeath: (snake: Snake, killedByPlayer: boolean, killer: Snake) => void;
  /**
   * "Feast" death — fires for EVERY snake death (not just player-credited).
   * Engine drives a screen-space color flash + light bot-kill celebration
   * scaled by distance + victim mass.
   */
  onDeathFlash?: (x: number, y: number, hue: number, victimMass: number) => void;
  /** A snake's tongue flicked — engine plays audio for the player only. */
  onTongueFlick?: (snake: Snake) => void;
  /** Player picked up a powerup — engine handles celebration + sound + HUD push. */
  onPowerupPickup?: (kind: PowerupKind, x: number, y: number) => void;
  /** Player's shield absorbed a fatal hit — engine handles burst + sound. */
  onShieldBreak?: (x: number, y: number) => void;
}

/**
 * World — owns gameplay state and runs the simulation.
 *
 * Ticks:
 *   1. AI brains set steer + boost.
 *   2. Snake physics + food.step() (ejected pellet velocity).
 *   3. Spatial hashes rebuilt.
 *   4. Food consumption with color-match multiplier.
 *   5. Snake-vs-snake collisions.
 *   6. Bot population maintenance.
 *
 * Effects (particles, screen shake) are surfaced via the WorldEffects
 * callback set — the World stays render-agnostic.
 */
export class World {
  public readonly snakes: Snake[] = [];
  public readonly food: FoodField;
  public readonly powerups: PowerupField;
  public player: Snake;
  public readonly particles = new ParticleSystem();

  /** Player's team hue — exported so HUD can show "your color". */
  public playerHue: number = TEAM_HUES[4]!; // cyan (200°) — distinctive default

  /** Custom display name for the player (defaults to 'YOU'). */
  public playerName = 'YOU';

  /** Sim clock, separate from real time so we can pause/respawn cleanly. */
  public time = 0;

  private readonly ai = new SnakeAI();
  private readonly rng: () => number;
  private nextId = 1;

  private readonly headHash = new SpatialHash(300);
  private readonly foodHash = new SpatialHash(200);
  private readonly queryBuf: number[] = [];

  private effects: WorldEffects;

  /** Accumulator for the leader-recompute tick (cheap, every 0.25s). */
  private leaderAccum = 0;

  constructor(
    effects: WorldEffects,
    opts: { seed?: number; playerHue?: number; playerPattern?: SkinPattern } = {},
  ) {
    const seed = opts.seed ?? 1337;
    this.rng = mulberry32(seed);
    this.effects = effects;
    this.food = new FoodField(seed ^ 0x9e3779b1);
    this.powerups = new PowerupField(seed ^ 0x517cc1b7);
    if (opts.playerHue !== undefined) this.playerHue = opts.playerHue;
    if (opts.playerPattern !== undefined) this.playerPattern = opts.playerPattern;

    this.player = this.spawnPlayer();
    this.snakes.push(this.player);
    this.applySpawnEffects(this.player);
    this.replenishBots();
  }

  /** Player's chosen skin pattern — overrides the makePlayerSkin default. */
  public playerPattern: SkinPattern = 'plasma';

  setEffects(effects: WorldEffects): void {
    this.effects = effects;
  }

  // ---------------------------------------------------------------------------
  // Spawn helpers
  // ---------------------------------------------------------------------------

  private spawnPlayer(): Snake {
    return new Snake({
      id: this.nextId++,
      isPlayer: true,
      name: this.playerName,
      skin: this.makePlayerSkin(this.playerHue),
      spawn: { x: WORLD.SIZE / 2, y: WORLD.SIZE / 2 },
      heading: 0,
      mass: radiusToMass(PLAYER.START_RADIUS) + 80,
    });
  }

  /** Apply a new player display name. Updates current snake + future respawns. */
  setPlayerName(name: string): void {
    const final = (name || 'YOU').slice(0, 16);
    this.playerName = final;
    this.player.name = final;
  }

  /** Respawn the player as a fresh snake. The old corpse is dropped already. */
  respawnPlayer(): void {
    const idx = this.snakes.indexOf(this.player);
    if (idx >= 0) this.snakes.splice(idx, 1);
    this.player = this.spawnPlayer();
    this.snakes.push(this.player);
    this.applySpawnEffects(this.player);
    // Fresh powerup field on respawn so the player starts clean.
    this.powerups.reset(this.time);
  }

  /** Spawn signature applied to every newly-spawned snake. */
  private applySpawnEffects(snake: Snake): void {
    snake.spawnTime = this.time;
    snake.invincibleUntil = this.time + 1.5;
    // Bright flash burst at the spawn location, tinted to team hue.
    this.particles.burst(snake.head.x, snake.head.y, snake.teamHue, 18, 260);
  }

  // ---------------------------------------------------------------------------
  // Public sim API
  // ---------------------------------------------------------------------------

  update(dt: number): void {
    this.time += dt;

    // 1) AI steers. Passing `this.player` lets the bot's wander/edge
    //    logic congregate around the player rather than drifting off
    //    to the map borders.
    for (let i = 0; i < this.snakes.length; i++) {
      const s = this.snakes[i]!;
      if (!s.alive || s.isPlayer) continue;
      this.ai.step(s, dt, this.snakes, this.food, this.player);
    }

    // 2) Snake physics + food projectiles + tongue scheduling.
    for (let i = 0; i < this.snakes.length; i++) {
      const s = this.snakes[i]!;
      if (!s.alive) continue;
      s.update(dt);
      // Periodically schedule a tongue flick. First scheduling lazily.
      if (s.nextFlickAt === 0) {
        s.nextFlickAt = this.time + 1.5 + this.rng() * 3;
      } else if (this.time >= s.nextFlickAt) {
        s.triggerFlick();
        s.nextFlickAt = this.time + 2 + this.rng() * 4;
        this.effects.onTongueFlick?.(s);
      }
    }
    this.food.step(dt, this.time);
    this.particles.step(dt);
    // Powerup spawning — gated by interval + max-active cap.
    this.powerups.step(this.time, this.player.head.x, this.player.head.y);

    // 3) Spatial hashes.
    this.headHash.clear();
    this.foodHash.clear();
    for (let i = 0; i < this.snakes.length; i++) {
      const s = this.snakes[i]!;
      if (!s.alive) continue;
      this.headHash.insert(s.id, s.head.x, s.head.y);
    }
    const pellets = this.food.pellets;
    for (let i = 0; i < pellets.length; i++) {
      const p = pellets[i]!;
      this.foodHash.insert(i, p.x, p.y);
    }

    // 4) Eating (color-match economy).
    this.processFoodEating();

    // 4.25) Boost trail — drops pellets + particles behind boosting snakes.
    this.processBoostTrail(dt);

    // 4.5) Powerup pickup — player only.
    this.processPowerupPickup();

    // 5) Collisions.
    this.processSnakeCollisions();

    // 6) Bot replenishment.
    this.replenishBots();

    // 7) Leader designation — recompute "who's #1" at 4 Hz so the alpha
    //    aura tracks rank reorder without iterating every snake every frame.
    this.leaderAccum += dt;
    if (this.leaderAccum >= 0.25) {
      this.leaderAccum = 0;
      this.recomputeLeader();
    }
  }

  /** Mark exactly one snake (the heaviest) as the leader. */
  private recomputeLeader(): void {
    let best: Snake | null = null;
    for (let i = 0; i < this.snakes.length; i++) {
      const s = this.snakes[i]!;
      if (!s.alive) { s.isLeader = false; continue; }
      if (!best || s.mass > best.mass) best = s;
    }
    for (let i = 0; i < this.snakes.length; i++) {
      const s = this.snakes[i]!;
      s.isLeader = s === best;
    }
  }

  /** Called when the player presses W. Emits one ejected pellet. */
  ejectFromPlayer(): boolean {
    const p = this.player;
    if (!p.alive) return false;
    if (p.mass < EJECT.MIN_MASS) return false;
    p.removeMass(EJECT.COST_MASS);
    const hx = Math.cos(p.heading);
    const hy = Math.sin(p.heading);
    this.food.ejectAt({
      fromX: p.head.x + hx * p.radius * 1.1,
      fromY: p.head.y + hy * p.radius * 1.1,
      headingX: hx,
      headingY: hy,
      hue: p.teamHue,
      mass: EJECT.COST_MASS * 0.85,
      ownerId: p.id,
      now: this.time,
    });
    return true;
  }

  // ---------------------------------------------------------------------------
  // Spawning
  // ---------------------------------------------------------------------------

  /**
   * Replenish bots:
   *   1. Ensure every one of the 7 RIVAL_PROFILES has an alive snake — if
   *      one died, spawn it back with the *same identity* (same name, hue,
   *      and visual signature).
   *   2. Ensure FILLER_COUNT anonymous filler bots are alive — small fry
   *      that populate the world for action and provide easy mass.
   */
  private replenishBots(): void {
    const aliveRivals = new Set<number>();
    let aliveFillers = 0;
    for (let i = 0; i < this.snakes.length; i++) {
      const s = this.snakes[i]!;
      if (!s.alive || s.isPlayer) continue;
      if (s.profileIdx !== undefined) aliveRivals.add(s.profileIdx);
      else aliveFillers++;
    }
    for (let i = 0; i < RIVAL_PROFILES.length; i++) {
      if (!aliveRivals.has(i)) this.spawnRival(i);
    }
    while (aliveFillers < FILLER_COUNT) {
      this.spawnFiller();
      aliveFillers++;
    }
    // GC dead snakes occasionally so the array doesn't grow unboundedly.
    const totalCeiling = RIVAL_PROFILES.length + FILLER_COUNT;
    if (this.snakes.length > totalCeiling * 3 + 4) this.gcDeadSnakes();
  }

  /** Spawn a specific named rival from its profile. Used on death-respawn. */
  private spawnRival(profileIdx: number): void {
    const profile = RIVAL_PROFILES[profileIdx]!;
    const skin: SnakeSkin = { hue: profile.hue, ...profile.skin };
    // Rivals start fatter than fillers — they're the boss-class entities
    // and the player needs to feel "these are real threats" from the
    // first second of play. Bumped from 900-1600 → 1700-3000: a fresh
    // rival is now visibly 1.5-3× the player's start mass (~1100), so
    // even with perfect early play, the player has someone to chase.
    const mass = randRange(this.rng, 1700, 3000);
    const { x, y } = this.pickSpawnPoint();
    const rival = new Snake({
      id: this.nextId++,
      isPlayer: false,
      name: profile.name,
      skin,
      spawn: { x, y },
      heading: this.rng() * Math.PI * 2,
      mass,
      profileIdx,
      personality: profile.personality,
    });
    this.snakes.push(rival);
    this.ai.attach(rival);
    this.applySpawnEffects(rival);
  }

  /**
   * Spawn an anonymous filler bot — random hue/name/archetype, low starting
   * mass so they can't compete with rivals on the leaderboard. They serve
   * as huntable prey + action filler.
   *
   * Personality is derived from a hash of the name so each named bot
   * consistently behaves the same way every respawn — "cobra" is always
   * the headhunter, "wisp" is always the timid forager. Distribution is
   * weighted toward `normal`/`cautious` so the world isn't 100% aggressors.
   */
  private spawnFiller(): void {
    const name = FILLER_NAMES[Math.floor(this.rng() * FILLER_NAMES.length)]!;
    const teamHue = TEAM_HUES[Math.floor(this.rng() * TEAM_HUES.length)]!;
    const skin = this.makeFillerSkin(name, teamHue);
    // Fillers spawn small — never enough to threaten the leaderboard
    // initially, BUT passive growth (see Snake.update) will pump them
    // over time so they don't stay forever-trivial. Bumped the floor
    // 180 → 320 so a fresh filler isn't a free 2-bite snack: still
    // huntable, but the player has to work a little.
    const mass = randRange(this.rng, 320, 650);
    const { x, y } = this.pickSpawnPoint();
    const personality = this.fillerPersonalityFor(name);
    const filler = new Snake({
      id: this.nextId++,
      isPlayer: false,
      name,
      skin,
      spawn: { x, y },
      heading: this.rng() * Math.PI * 2,
      mass,
      personality,
    });
    this.snakes.push(filler);
    this.ai.attach(filler);
    this.applySpawnEffects(filler);
  }

  /**
   * Deterministic personality from filler name. Buckets:
   *   0..49  → 'cautious'   (50%, dominant — most fillers are food-gatherers)
   *   50..79 → 'normal'     (30%, balanced)
   *   80..94 → 'aggressive' (15%, threats)
   *   95..99 → 'hunter'     (5%,  rare apex predator filler)
   * This distribution keeps the world feeling alive without flooding it
   * with chasers, while still ensuring some filler bots actively hunt the player.
   */
  private fillerPersonalityFor(name: string): Personality {
    const bucket = hashStringToSeed(name) % 100;
    if (bucket < 50) return 'cautious';
    if (bucket < 80) return 'normal';
    if (bucket < 95) return 'aggressive';
    return 'hunter';
  }

  /** Pick a spawn point that isn't too close to the player. */
  private pickSpawnPoint(): { x: number; y: number } {
    const margin = WORLD.EDGE_PADDING * 2;
    const playerHead = this.player.head;
    let x = 0, y = 0;
    for (let attempt = 0; attempt < 8; attempt++) {
      x = randRange(this.rng, margin, WORLD.SIZE - margin);
      y = randRange(this.rng, margin, WORLD.SIZE - margin);
      const dx = x - playerHead.x;
      const dy = y - playerHead.y;
      if (dx * dx + dy * dy > 1000 * 1000) break;
    }
    return { x, y };
  }

  private gcDeadSnakes(): void {
    for (let i = this.snakes.length - 1; i >= 0; i--) {
      const s = this.snakes[i]!;
      if (!s.alive && !s.isPlayer) {
        this.ai.detach(s.id);
        this.snakes.splice(i, 1);
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Skins
  // ---------------------------------------------------------------------------

  private makePlayerSkin(hue: number): SnakeSkin {
    // Player skin — flagship design: clean wave + plasma signature pattern
    // (cyan + magenta electric arcs, matching the brand identity from the
    // skins reference art).
    return {
      hue,
      sat: 100,
      baseLight: 56,
      lightAmp: 28,
      waveFreq: 5.5,
      waveSpeed: 0.55,
      wavePhase: 0,
      darkBandDepth: 0,
      darkBandFreq: 0,
      darkBandSpeed: 0,
      darkBandPhase: 0,
      orbCount: 5,
      orbSpeed: 0.18,
      pattern: this.playerPattern,
    };
  }

  /**
   * Filler skin — random procedural variation. The 5 archetypes mean fillers
   * still look diverse, but they're never as polished as the 7 rivals'
   * hand-tuned signatures. Combined with their smaller size, fillers always
   * read as "the small fry" against the boss-class rivals.
   */
  private makeFillerSkin(name: string, teamHue: number): SnakeSkin {
    const rng = mulberry32(hashStringToSeed(name) ^ this.nextId);

    type Archetype = {
      lightAmp: [number, number];
      waveFreq: [number, number];
      waveSpeed: [number, number];
      darkBandDepth: [number, number];
      darkBandFreq: [number, number];
      darkBandSpeed: [number, number];
    };
    const archetypes: Archetype[] = [
      // 0 — "Glow worm"
      { lightAmp: [22, 30], waveFreq: [3, 5],  waveSpeed: [0.25, 0.45], darkBandDepth: [0, 0],     darkBandFreq: [0, 0],   darkBandSpeed: [0, 0] },
      // 1 — "Striker"
      { lightAmp: [26, 34], waveFreq: [6, 9],  waveSpeed: [0.8, 1.2],   darkBandDepth: [0, 0],     darkBandFreq: [0, 0],   darkBandSpeed: [0, 0] },
      // 2 — "Tiger"
      { lightAmp: [20, 28], waveFreq: [4, 6],  waveSpeed: [0.4, 0.7],   darkBandDepth: [0.45, 0.7], darkBandFreq: [10, 16], darkBandSpeed: [0, 0.15] },
      // 3 — "Pulse drift"
      { lightAmp: [14, 22], waveFreq: [2, 4],  waveSpeed: [0.2, 0.4],   darkBandDepth: [0.55, 0.85], darkBandFreq: [4, 7],   darkBandSpeed: [0.05, 0.25] },
      // 4 — "Lattice"
      { lightAmp: [24, 32], waveFreq: [5, 8],  waveSpeed: [0.5, 0.9],   darkBandDepth: [0.3, 0.5],  darkBandFreq: [14, 22], darkBandSpeed: [0.3, 0.6] },
    ];
    const archetype = archetypes[Math.floor(rng() * archetypes.length)]!;
    const pick = ([lo, hi]: [number, number]) => lo + rng() * (hi - lo);

    return {
      hue: teamHue,
      sat: 95 + rng() * 5,
      baseLight: 50 + rng() * 10,
      lightAmp: pick(archetype.lightAmp),
      waveFreq: pick(archetype.waveFreq),
      waveSpeed: pick(archetype.waveSpeed),
      wavePhase: rng() * Math.PI * 2,
      darkBandDepth: pick(archetype.darkBandDepth),
      darkBandFreq: pick(archetype.darkBandFreq),
      darkBandSpeed: pick(archetype.darkBandSpeed),
      darkBandPhase: rng() * Math.PI * 2,
      // Fillers don't get orbs — orbs are a "signature" feature reserved
      // for the boss-class rivals and the player.
      orbCount: 0,
      orbSpeed: 0,
    };
  }

  // ---------------------------------------------------------------------------
  // Eating (color-match economy)
  // ---------------------------------------------------------------------------

  private processFoodEating(): void {
    const buf = this.queryBuf;
    const pellets = this.food.pellets;
    for (let i = 0; i < this.snakes.length; i++) {
      const s = this.snakes[i]!;
      if (!s.alive) continue;
      const eatRange = s.radius * FOOD.EAT_RANGE_FACTOR;
      buf.length = 0;
      this.foodHash.queryCircle(s.head.x, s.head.y, eatRange + FOOD.MAX_RADIUS * 1.4, buf);
      const eatR = eatRange;
      for (let k = 0; k < buf.length; k++) {
        const idx = buf[k]!;
        const p = pellets[idx]!;
        // Grace: a freshly-ejected pellet cannot be eaten by its owner.
        if (this.time < p.eatableAt && p.ownerId === s.id) continue;
        const dx = p.x - s.head.x;
        const dy = p.y - s.head.y;
        const sum = eatR + p.radius;
        if (dx * dx + dy * dy <= sum * sum) {
          // Self-pellet hard skip: a snake CANNOT eat its own trail /
          // eject AT ALL. The earlier compromise (off-color multiplier
          // for self) still returned 0.55× mass, so circling your own
          // tail was a slow-but-real profit loop. Now self-pellets are
          // permanent obstacles for the owner — they exist purely as
          // bait/food for OTHER snakes, which preserves the tactical
          // "burn rubber to feed allies / lure enemies" depth without
          // any self-feed exploit.
          if (p.ownerId === s.id) continue;
          const colorMult = matchMultiplier(s.teamHue, p.hue);
          // Double-mass powerup multiplies on top of the color match mult.
          const doubleMult = s.doubleMassRemaining > 0 ? POWERUP.DOUBLE_MASS_MULT : 1;
          const finalMult = colorMult * doubleMult;
          s.addMass(p.mass * finalMult);
          s.triggerChomp();
          this.effects.onEat(s, p.x, p.y, p.hue, p.mass, finalMult);
          if (colorMult === COLOR_MATCH.PERFECT_MULT) {
            s.triggerPulse();
            // Spawn a digesting orb in the eater's belly — for ANY snake
            // (player or bot). Visual feedback "I ate my color" is part of
            // the world's identity, not just the player's HUD.
            s.addDigestingOrb(p.hue);
            // Only fire the engine-level effects (audio/combo/celebration)
            // for the PLAYER's perfect eats.
            if (s.isPlayer) this.effects.onMatchEat(p.x, p.y, p.hue);
          }
          this.food.respawn(idx);
        }
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Collisions
  // ---------------------------------------------------------------------------

  private processSnakeCollisions(): void {
    for (let i = 0; i < this.snakes.length; i++) {
      const s = this.snakes[i]!;
      if (!s.alive) continue;
      // Respawn invincibility — the snake is shielded for ~1.5s after spawn.
      if (s.isInvincible(this.time)) continue;
      // We used to filter candidates through headHash (head-positioned),
      // but giants have body segments that can extend WAY past their head
      // — a small attacker querying around its own head would miss those
      // bodies entirely and pass through. With ~15 snakes max, iterating
      // all of them is ~225 checks/frame: negligible and correct.
      let killer: Snake | null = null;
      for (let k = 0; k < this.snakes.length; k++) {
        if (k === i) continue;
        const o = this.snakes[k]!;
        if (!o.alive) continue;
        if (this.headHitsBody(s, o)) {
          killer = o;
          break;
        }
      }
      if (killer) {
        // Shield absorbs the fatal hit — consume the shield instead of dying.
        if (s.shielded) {
          s.shielded = false;
          // Brief invincibility window so we don't immediately re-collide on
          // the next frame.
          s.invincibleUntil = this.time + 0.8;
          // Bounce the head slightly away from the killer so collision clears.
          const dx = s.head.x - killer.head.x;
          const dy = s.head.y - killer.head.y;
          const len = Math.hypot(dx, dy) || 1;
          s.head.x += (dx / len) * (s.radius + 12);
          s.head.y += (dy / len) * (s.radius + 12);
          this.effects.onShieldBreak?.(s.head.x, s.head.y);
        } else {
          this.killSnake(s, killer);
        }
      }
    }
  }

  private headHitsBody(attacker: Snake, victim: Snake): boolean {
    const ax = attacker.head.x;
    const ay = attacker.head.y;
    const ar = attacker.radius * 0.85;
    const segs = victim.segments;
    const totalLen = segs.length;
    // Check EVERY segment (not every other) — on giants the visual body is
    // continuous but discrete segment centers can have gaps wider than a
    // small attacker's radius, letting them slip through with i+=2.
    for (let i = 1; i < totalLen; i++) {
      const seg = segs[i]!;
      const u = i / (totalLen - 1);
      const segR = victim.widthAt(u);
      const dx = ax - seg.x;
      const dy = ay - seg.y;
      const sum = ar + segR;
      if (dx * dx + dy * dy <= sum * sum) return true;
    }
    return false;
  }

  // ---------------------------------------------------------------------------
  // Boost trail — spawns pellets + particles behind boosting snakes
  // ---------------------------------------------------------------------------

  private processBoostTrail(dt: number): void {
    void dt;
    for (let i = 0; i < this.snakes.length; i++) {
      const s = this.snakes[i]!;
      if (!s.alive) continue;
      const segs = s.segments;
      if (segs.length < 2) continue;
      const tail = segs[segs.length - 1]!;

      // 1. Trail pellets — drain the boostMassBurned accumulator into
      //    discrete pellets behind the tail. Each pellet is in the snake's
      //    team hue so the trail visually identifies the burner.
      while (s.boostMassBurned >= BOOST_TRAIL.BURN_PER_PELLET) {
        s.boostMassBurned -= BOOST_TRAIL.BURN_PER_PELLET;
        // Spawn position: at the tail, slightly jittered. ownerId stamps
        // the pellet so the eating logic can apply the self-feed
        // penalty — without it the snake could boost-then-circle to
        // farm its own trail for a NET POSITIVE mass return
        // (6 burned → 6×PERFECT_MULT=15 back = +9 free mass per pellet).
        this.food.dropAt({
          x: tail.x,
          y: tail.y,
          hue: s.teamHue,
          mass: BOOST_TRAIL.BURN_PER_PELLET,
          ownerId: s.id,
        });
      }

      // 2. Ignition particles — backward-facing, emitted at the tail
      //    while ANY boost (regular or surge) is active.
      const isBoosting = s.boosting || s.surgeRemaining > 0;
      if (!isBoosting) continue;
      const particleInterval = 1 / BOOST_TRAIL.PARTICLE_RATE;
      while (s.boostParticleAccum >= particleInterval) {
        s.boostParticleAccum -= particleInterval;
        // Spawn point biased opposite the heading so the burst reads as
        // "trailing behind". burst() gives the particles random outward
        // velocity from that point — combined with the offset spawn it
        // looks like exhaust from the tail.
        const backAngle = s.heading + Math.PI;
        this.particles.burst(
          tail.x + Math.cos(backAngle) * 6,
          tail.y + Math.sin(backAngle) * 6,
          s.teamHue,
          1,
          BOOST_TRAIL.PARTICLE_SPEED * 0.35,
        );
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Powerup pickup
  // ---------------------------------------------------------------------------

  private processPowerupPickup(): void {
    // Player-only pickup for now — bots ignore powerups. Keeps the meta
    // focused on the player feeling powerful, not bots stealing buffs.
    const p = this.player;
    if (!p.alive) return;
    const range = POWERUP.PICKUP_RANGE + p.radius;
    const rangeSq = range * range;
    const list = this.powerups.powerups;
    for (let i = list.length - 1; i >= 0; i--) {
      const pu = list[i]!;
      const dx = pu.x - p.head.x;
      const dy = pu.y - p.head.y;
      if (dx * dx + dy * dy > rangeSq) continue;
      this.applyPowerup(p, pu);
      list.splice(i, 1);
    }
  }

  private applyPowerup(snake: Snake, pu: Powerup): void {
    switch (pu.kind) {
      case 'surge':
        snake.surgeRemaining = Math.max(snake.surgeRemaining, POWERUP.SURGE_DURATION);
        break;
      case 'double':
        snake.doubleMassRemaining = Math.max(snake.doubleMassRemaining, POWERUP.DOUBLE_DURATION);
        break;
      case 'shield':
        snake.shielded = true;
        break;
    }
    this.effects.onPowerupPickup?.(pu.kind, pu.x, pu.y);
  }

  private killSnake(s: Snake, killedBy: Snake): void {
    s.kill();
    // If the leader fell, the next computeLeaderboard tick will rotate the
    // crown — clear the flag now so render doesn't keep drawing the aura
    // on the corpse before the leaderboard ticks.
    s.isLeader = false;

    // --- "Feast" death-burst ---
    // Each body segment is dropped as a food pellet WITH an outward
    // velocity vector, so the body visibly explodes outward and the pellets
    // settle naturally via Food.step drag. Outward direction is computed
    // from the body centroid → segment so the chunks fly away from the
    // dead snake's core rather than collapsing inward.
    //
    // Death drops 100% of mass (was 70%) — a kill is the headline
    // economic event of the match, and the corpse should be a visible
    // feast that other snakes race to. Plus we drop EXTRA pellets
    // between body segments so a big snake leaves dozens of pickups
    // instead of just one-per-segment.
    const drop = s.mass;
    const segCount = s.segments.length;
    // Inflate the pellet count to 1.5× segment count — fills gaps
    // between segments so a giant snake's corpse looks like a real
    // explosion of food, not a sparse trail.
    const dropCount = Math.max(1, Math.floor(segCount * 1.5));
    const perSegment = drop / dropCount;
    // Centroid pass.
    let cx = 0, cy = 0;
    for (let i = 0; i < segCount; i++) {
      const seg = s.segments[i]!;
      cx += seg.x;
      cy += seg.y;
    }
    cx /= Math.max(1, segCount);
    cy /= Math.max(1, segCount);
    const speedRange = DEATH_BURST.SPEED_MAX - DEATH_BURST.SPEED_MIN;
    // Drop dropCount pellets — 1 per segment plus a half-segment-worth
    // of extras interpolated at random points along the body so the
    // pellet field looks dense and chaotic, not gridded.
    for (let i = 0; i < dropCount; i++) {
      // Pick a segment by floating index so the extras land between
      // body points instead of stacking on the same segment.
      const segIdx = Math.min(segCount - 1, Math.floor((i / dropCount) * segCount));
      const seg = s.segments[segIdx]!;
      // Random offset around the segment so corpses don't read as a
      // perfect string of pearls.
      const ox0 = (this.rng() - 0.5) * 14;
      const oy0 = (this.rng() - 0.5) * 14;
      let ox = seg.x + ox0 - cx;
      let oy = seg.y + oy0 - cy;
      let ol = Math.hypot(ox, oy);
      if (ol < 1e-3) {
        // Degenerate (curled-up snake) — pick a random outward direction.
        const a = this.rng() * Math.PI * 2;
        ox = Math.cos(a); oy = Math.sin(a); ol = 1;
      }
      const speed = DEATH_BURST.SPEED_MIN + this.rng() * speedRange;
      const jitter = (this.rng() - 0.5) * 0.6;        // small lateral spread
      const vx = (ox / ol) * speed + (-oy / ol) * speed * jitter;
      const vy = (oy / ol) * speed + ( ox / ol) * speed * jitter;
      // Death-burst pellets are NOT stamped with ownerId — the corpse
      // is up for grabs by anyone, including (eventually) the killer.
      this.food.dropAt({
        x: seg.x + ox0,
        y: seg.y + oy0,
        hue: s.teamHue,
        mass: perSegment,
        vx, vy,
      });
    }
    // Body shatter — burst at every other segment, in the same outward
    // direction as the pellets, so particles and pellets read as a single
    // explosion rather than two layered effects.
    for (let i = 0; i < segCount; i += 2) {
      const seg = s.segments[i]!;
      this.particles.burst(seg.x, seg.y, s.teamHue, 3, 180);
    }
    // Bigger head burst — the "explosion" focal point.
    this.particles.burst(
      s.head.x,
      s.head.y,
      s.teamHue,
      COLOR_MATCH.BURST_COUNT * 2,
      COLOR_MATCH.BURST_SPEED * 1.4,
    );
    // Death-flash callback — engine drives the screen-space color tint.
    this.effects.onDeathFlash?.(s.head.x, s.head.y, s.teamHue, s.mass);
    const killedByPlayer = killedBy.isPlayer;
    this.effects.onSnakeDeath(s, killedByPlayer, killedBy);
  }

  // ---------------------------------------------------------------------------
  // Decay enforcement — already in Snake.update; nothing to do here.
  // Kept as a stub seam in case the Economist wants global modifiers.
  // ---------------------------------------------------------------------------
  // (intentional empty section)
  // ---------------------------------------------------------------------------
}

